import React, {useState} from 'react';
import {
  Button,
  Checkbox,
  Collapse,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  Tooltip,
} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {useMutation, useQuery} from '@apollo/client';
import {DeleteOutlined, PlusOutlined, QuestionCircleOutlined} from '@ant-design/icons';
import {useTranslation} from 'react-i18next';
import {
  GET_POD_EXEMPT_DICT_ITEMS_FOR_MANAGE,
  PodExemptDictItemsForManageResponse,
} from '../../../graphql/podExempt/queries';
import {
  DELETE_POD_EXEMPT_DICT_ITEM,
  DISABLE_POD_EXEMPT_DICT_ITEM,
  SAVE_POD_EXEMPT_DICT_ITEM,
  SavePodExemptDictItemInput,
} from '../../../graphql/podExempt/mutations';
import {
  parseDictExt,
  PodExemptDictItemType,
  PodExemptDictType,
  PodExemptPhotoSlot,
} from '../../../graphql/podExempt/types';
import {translateGraphQLError} from '../../../utils/graphqlUtils';

/** 与后端 CODE_PATTERN 一致 */
const CODE_PATTERN = /^[A-Z][A-Z0-9_]{0,63}$/;
/** 槽位 code 落 `pod_exempt_material.category`（VARCHAR(32)），故长度上限比字典 code 更短 */
const SLOT_CODE_PATTERN = /^[A-Z][A-Z0-9_]{0,31}$/;
const SLOT_CODE_MAX_LENGTH = 32;

/** 结构化属性表单的取值（photoSlots / requireVideo 不直接提交，保存时合成 extJson） */
interface DictFormValues extends SavePodExemptDictItemInput {
  photoSlots?: PodExemptPhotoSlot[];
  requireVideo?: boolean;
}

/** 结构化表单认识的 extJson 键；其余键由「原始 JSON」逃生口原样保留 */
const MANAGED_EXT_KEYS = ['photoSlots', 'requireVideo'];

/** 审核后台：字典管理（分类体系纯配置——产品调整地址类型/原因/驳回模板零发版）。 */
const PodExemptDictManagePage: React.FC = () => {
  const {t} = useTranslation();
  const [dictType, setDictType] = useState<PodExemptDictType>('ADDRESS_TYPE');
  const [editing, setEditing] = useState<PodExemptDictItemType | 'NEW' | null>(null);
  /** 保存中防重复点击（快速双击「确定」会发两次 upsert） */
  const [saving, setSaving] = useState(false);
  /**
   * 结构化表单不认识的 extJson 键（历史配置、数据组约定等），保存时原样写回，
   * 避免「用管理页编辑一次就把别人的键洗掉」。
   */
  const [extUnknownKeys, setExtUnknownKeys] = useState<Record<string, unknown>>({});
  /** 用户是否动过「原始 JSON」逃生口；动过则以其内容为准，覆盖上方结构化配置 */
  const [rawDirty, setRawDirty] = useState(false);
  const [form] = Form.useForm<DictFormValues>();

  const {data, loading, refetch} = useQuery<PodExemptDictItemsForManageResponse>(
    GET_POD_EXEMPT_DICT_ITEMS_FOR_MANAGE,
    {variables: {dictType}, fetchPolicy: 'cache-and-network'},
  );
  const {data: addressTypesData} = useQuery<PodExemptDictItemsForManageResponse>(
    GET_POD_EXEMPT_DICT_ITEMS_FOR_MANAGE,
    {variables: {dictType: 'ADDRESS_TYPE'}, skip: dictType !== 'REASON'},
  );
  const [saveItem] = useMutation(SAVE_POD_EXEMPT_DICT_ITEM);
  const [disableItem] = useMutation(DISABLE_POD_EXEMPT_DICT_ITEM);
  const [deleteItem] = useMutation(DELETE_POD_EXEMPT_DICT_ITEM);

  const handleDelete = async (record: PodExemptDictItemType) => {
    try {
      await deleteItem({variables: {dictType: record.dictType, code: record.code}});
      message.success(t('podExempt.dict.deleteSuccess'));
      refetch();
    } catch (e) {
      message.error(translateGraphQLError(e, 'podExempt.dict.deleteFailed'));
    }
  };

  const openEdit = (item: PodExemptDictItemType | 'NEW') => {
    setEditing(item);
    setRawDirty(false);
    if (item === 'NEW') {
      form.resetFields();
      // parentCode 显式给 null：ADDRESS_TYPE 的「适用上报维度」默认落在「不限」而非空选
      form.setFieldsValue({
        dictType,
        parentCode: null,
        status: 1,
        sortNo: 0,
        photoSlots: [],
        requireVideo: false,
      });
      setExtUnknownKeys({});
      return;
    }
    const ext = parseDictExt(item.extJson);
    const unknown: Record<string, unknown> = {};
    Object.entries(ext as Record<string, unknown>).forEach(([key, value]) => {
      if (!MANAGED_EXT_KEYS.includes(key)) {
        unknown[key] = value;
      }
    });
    setExtUnknownKeys(unknown);
    form.setFieldsValue({
      dictType: item.dictType,
      code: item.code,
      parentCode: item.parentCode,
      nameZh: item.nameZh,
      nameEn: item.nameEn,
      nameEs: item.nameEs,
      extJson: item.extJson,
      status: item.status,
      sortNo: item.sortNo,
      photoSlots: Array.isArray(ext.photoSlots) ? ext.photoSlots : [],
      requireVideo: ext.requireVideo === true,
    });
  };

  /** 结构化字段 → extJson 字符串；无任何属性时回 null，避免写入 `{}` 噪声 */
  const composeExtJson = (values: DictFormValues): string | null => {
    const slots = (values.photoSlots ?? [])
      .map((slot) => ({
        code: (slot?.code ?? '').trim(),
        nameZh: (slot?.nameZh ?? '').trim(),
        nameEn: (slot?.nameEn ?? '').trim(),
        nameEs: (slot?.nameEs ?? '').trim(),
        required: slot?.required === true,
      }))
      .filter((slot) => slot.code.length > 0);
    const composed: Record<string, unknown> = {...extUnknownKeys};
    if (slots.length > 0) {
      composed.photoSlots = slots;
    } else {
      delete composed.photoSlots;
    }
    if (values.requireVideo === true) {
      composed.requireVideo = true;
    } else {
      delete composed.requireVideo;
    }
    return Object.keys(composed).length > 0 ? JSON.stringify(composed) : null;
  };

  const handleSave = async () => {
    // validateFields 的 reject 也要接住，否则校验失败产生 unhandled rejection
    let values: DictFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const {photoSlots: _photoSlots, requireVideo: _requireVideo, extJson, ...rest} = values;
    // REASON 走结构化合成；其它类型不渲染属性区，原值原样带回
    const useRaw = dictType !== 'REASON' || rawDirty;
    const finalExtJson = useRaw ? (extJson ?? '').trim() || null : composeExtJson(values);
    setSaving(true);
    try {
      await saveItem({variables: {input: {...rest, dictType, extJson: finalExtJson}}});
      message.success(t('podExempt.common.saveSuccess'));
      setEditing(null);
      refetch();
    } catch (e) {
      message.error(translateGraphQLError(e, 'podExempt.dict.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleEnable = async (item: PodExemptDictItemType) => {
    try {
      // 复用保存接口回置启用（后端无独立 enable，save 幂等覆盖）
      await saveItem({
        variables: {
          input: {
            dictType: item.dictType,
            code: item.code,
            parentCode: item.parentCode,
            nameZh: item.nameZh,
            nameEn: item.nameEn,
            nameEs: item.nameEs,
            extJson: item.extJson,
            status: 1,
            sortNo: item.sortNo,
          },
        },
      });
      message.success(t('podExempt.dict.enableSuccess'));
      refetch();
    } catch (e) {
      message.error(translateGraphQLError(e, 'podExempt.dict.saveFailed'));
    }
  };

  const handleDisable = async (item: PodExemptDictItemType) => {
    try {
      await disableItem({variables: {dictType: item.dictType, code: item.code}});
      message.success(t('podExempt.dict.disabled'));
      refetch();
    } catch (e) {
      message.error(translateGraphQLError(e, 'podExempt.dict.disableFailed'));
    }
  };

  /** 列表「属性」列：REASON 列出槽位与视频要求，其它类型仍显示原始串 */
  const renderExtSummary = (value: string | null): React.ReactNode => {
    if (dictType !== 'REASON') {
      return value ?? '-';
    }
    const ext = parseDictExt(value);
    const slots = (ext.photoSlots ?? []).filter((slot) => (slot.code ?? '').trim().length > 0);
    if (slots.length === 0 && ext.requireVideo !== true) {
      return '-';
    }
    return (
      <Space size={4} wrap>
        {slots.map((slot, index) => (
          <Tag key={`${slot.code}-${index}`} color={slot.required ? 'blue' : undefined}>
            {`${index + 1}. ${slot.nameZh?.trim() || slot.code}`}
            {slot.required ? ' *' : ''}
          </Tag>
        ))}
        {ext.requireVideo === true && <Tag color="purple">{t('podExempt.dict.requireVideo')}</Tag>}
      </Space>
    );
  };

  const columns: ColumnsType<PodExemptDictItemType> = [
    {title: 'Code', dataIndex: 'code', width: 200},
    {title: t('podExempt.dict.nameZh'), dataIndex: 'nameZh', width: 160},
    {title: 'EN', dataIndex: 'nameEn', width: 160, render: (v) => v ?? '-'},
    {title: 'ES', dataIndex: 'nameEs', width: 160, render: (v) => v ?? '-'},
    ...(dictType === 'REASON'
      ? [{title: t('podExempt.dict.parentCode'), dataIndex: 'parentCode', width: 140, render: (v: string | null) => v ?? '-'} as const]
      : []),
    ...(dictType === 'ADDRESS_TYPE'
      ? [
          {
            title: t('podExempt.dict.applyScope'),
            dataIndex: 'parentCode',
            width: 120,
            render: (v: string | null) =>
              v === 'SINGLE'
                ? t('podExempt.scope.single')
                : v === 'FENCE'
                  ? t('podExempt.scope.fence')
                  : t('podExempt.dict.applyScopeAll'),
          } as const,
        ]
      : []),
    ...(dictType === 'REJECT'
      ? []
      : [
          {
            title: t('podExempt.dict.extJson'),
            dataIndex: 'extJson',
            ellipsis: dictType !== 'REASON',
            render: (v: string | null) => renderExtSummary(v),
          } as const,
        ]),
    {
      title: t('podExempt.field.status'),
      dataIndex: 'status',
      width: 90,
      render: (v: number) =>
        v === 1 ? <Tag color="success">{t('podExempt.dict.enabled')}</Tag> : <Tag>{t('podExempt.dict.disabledStatus')}</Tag>,
    },
    {title: t('podExempt.dict.sortNo'), dataIndex: 'sortNo', width: 80},
    {
      title: t('podExempt.common.actions'),
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openEdit(record)}>
            {t('podExempt.common.edit')}
          </Button>
          {record.status === 1 ? (
            <Popconfirm
              title={t('podExempt.dict.disableConfirm')}
              onConfirm={() => handleDisable(record)}
            >
              <Button size="small" danger>
                {t('podExempt.dict.disable')}
              </Button>
            </Popconfirm>
          ) : (
            <Button size="small" onClick={() => handleEnable(record)}>
              {t('podExempt.dict.enableAction')}
            </Button>
          )}
          <Popconfirm
            title={t('podExempt.dict.deleteConfirm')}
            okButtonProps={{danger: true}}
            onConfirm={() => handleDelete(record)}
          >
            <Button size="small" danger>
              {t('podExempt.dict.deleteAction')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-2">
        <Typography.Title level={4} style={{margin: 0}}>
          {t('podExempt.dict.title')}
        </Typography.Title>
        <Button type="primary" onClick={() => openEdit('NEW')}>
          {t('podExempt.dict.add')}
        </Button>
      </div>
      <Tabs
        activeKey={dictType}
        onChange={(key) => setDictType(key as PodExemptDictType)}
        items={[
          {key: 'ADDRESS_TYPE', label: t('podExempt.dict.addressType')},
          {key: 'REASON', label: t('podExempt.dict.reason')},
          {key: 'REJECT', label: t('podExempt.dict.reject')},
        ]}
      />
      <Table<PodExemptDictItemType>
        rowKey={(record) => `${record.dictType}-${record.code}`}
        size="small"
        loading={loading}
        columns={columns}
        dataSource={data?.podExemptDictItemsForManage ?? []}
        pagination={false}
      />

      <Modal
        open={editing != null}
        title={editing === 'NEW' ? t('podExempt.dict.add') : t('podExempt.common.edit')}
        onCancel={() => setEditing(null)}
        onOk={handleSave}
        confirmLoading={saving}
        width={dictType === 'REASON' ? 760 : 520}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="code"
            label="Code"
            rules={[
              {required: true},
              {pattern: CODE_PATTERN, message: t('podExempt.dict.codePatternTip') ?? ''},
            ]}
          >
            <Input disabled={editing !== 'NEW'} placeholder="NO_DOOR_PLATE" />
          </Form.Item>
          {dictType === 'REASON' && (
            <Form.Item name="parentCode" label={t('podExempt.dict.parentCode')}>
              <Select
                allowClear
                options={(addressTypesData?.podExemptDictItemsForManage ?? []).map((item) => ({
                  value: item.code,
                  label: `${item.code} ${item.nameZh}`,
                }))}
              />
            </Form.Item>
          )}
          {dictType === 'ADDRESS_TYPE' && (
            <Form.Item
              name="parentCode"
              label={
                <span>
                  {t('podExempt.dict.applyScope')}
                  <Tooltip title={t('podExempt.dict.applyScopeTip')}>
                    <QuestionCircleOutlined className="ml-1 text-gray-400" />
                  </Tooltip>
                </span>
              }
            >
              <Radio.Group
                optionType="button"
                options={[
                  {value: null, label: t('podExempt.dict.applyScopeAll')},
                  {value: 'SINGLE', label: t('podExempt.scope.single')},
                  {value: 'FENCE', label: t('podExempt.scope.fence')},
                ]}
              />
            </Form.Item>
          )}
          <Form.Item name="nameZh" label={t('podExempt.dict.nameZh')} rules={[{required: true}]}>
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item name="nameEn" label="Name (EN)">
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item name="nameEs" label="Nombre (ES)">
            <Input maxLength={255} />
          </Form.Item>
          {dictType === 'REASON' && (
            <>
              <div className="mb-1 flex items-center">
                <span className="text-sm">{t('podExempt.dict.photoSlots')}</span>
                <Tooltip title={t('podExempt.dict.photoSlotsTip')}>
                  <QuestionCircleOutlined className="ml-1 text-gray-400" />
                </Tooltip>
              </div>
              <Form.List name="photoSlots">
                {(fields, {add, remove}) => (
                  <div className="mb-3">
                    {fields.length === 0 && (
                      <div className="mb-2 text-xs text-gray-400">{t('podExempt.dict.noSlot')}</div>
                    )}
                    {fields.map(({key, name}, index) => (
                      <div
                        key={key}
                        className="mb-2 rounded border border-gray-200 bg-gray-50 p-2"
                      >
                        <div className="mb-1 flex items-center justify-between">
                          <span className="text-xs text-gray-500">{`${index + 1}`}</span>
                          <Space size={8}>
                            <Form.Item
                              name={[name, 'required']}
                              valuePropName="checked"
                              noStyle
                            >
                              <Checkbox>{t('podExempt.dict.slotRequired')}</Checkbox>
                            </Form.Item>
                            <Tooltip title={t('podExempt.dict.removeSlot')}>
                              <Button
                                size="small"
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => remove(name)}
                              />
                            </Tooltip>
                          </Space>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Form.Item
                            name={[name, 'code']}
                            label={t('podExempt.dict.slotCode')}
                            className="mb-0 flex-1 min-w-[160px]"
                            rules={[
                              {required: true},
                              {
                                pattern: SLOT_CODE_PATTERN,
                                message: t('podExempt.dict.codePatternTip') ?? '',
                              },
                              {
                                validator: (_, value: string) => {
                                  const code = (value ?? '').trim();
                                  if (!code) {
                                    return Promise.resolve();
                                  }
                                  const all = (form.getFieldValue('photoSlots') ??
                                    []) as PodExemptPhotoSlot[];
                                  const duplicated = all.filter(
                                    (slot, i) => i !== name && (slot?.code ?? '').trim() === code,
                                  );
                                  return duplicated.length > 0
                                    ? Promise.reject(
                                        new Error(t('podExempt.dict.slotCodeDuplicated') ?? ''),
                                      )
                                    : Promise.resolve();
                                },
                              },
                            ]}
                          >
                            <Input
                              maxLength={SLOT_CODE_MAX_LENGTH}
                              placeholder="DOOR_FRONT"
                            />
                          </Form.Item>
                          <Form.Item
                            name={[name, 'nameZh']}
                            label={t('podExempt.dict.slotNameZh')}
                            className="mb-0 flex-1 min-w-[160px]"
                            rules={[{required: true}]}
                          >
                            <Input maxLength={64} placeholder="门口正面照" />
                          </Form.Item>
                          <Form.Item
                            name={[name, 'nameEn']}
                            label={t('podExempt.dict.slotNameEn')}
                            className="mb-0 flex-1 min-w-[160px]"
                          >
                            <Input maxLength={64} />
                          </Form.Item>
                          <Form.Item
                            name={[name, 'nameEs']}
                            label={t('podExempt.dict.slotNameEs')}
                            className="mb-0 flex-1 min-w-[160px]"
                          >
                            <Input maxLength={64} />
                          </Form.Item>
                        </div>
                      </div>
                    ))}
                    <Button
                      size="small"
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => add({required: true})}
                    >
                      {t('podExempt.dict.addSlot')}
                    </Button>
                  </div>
                )}
              </Form.List>
              <Form.Item name="requireVideo" valuePropName="checked" className="mb-2">
                <Checkbox>
                  {t('podExempt.dict.requireVideo')}
                  <Tooltip title={t('podExempt.dict.requireVideoTip')}>
                    <QuestionCircleOutlined className="ml-1 text-gray-400" />
                  </Tooltip>
                </Checkbox>
              </Form.Item>
              <Collapse
                ghost
                size="small"
                className="mb-2"
                items={[
                  {
                    key: 'raw',
                    label: <span className="text-xs text-gray-500">{t('podExempt.dict.rawJson')}</span>,
                    children: (
                      <>
                        <div className="mb-1 text-xs text-gray-400">
                          {t('podExempt.dict.rawJsonTip')}
                        </div>
                        <Form.Item
                          name="extJson"
                          className="mb-0"
                          rules={[
                            {
                              validator: (_, value: string) => {
                                if (!value || !rawDirty) {
                                  return Promise.resolve();
                                }
                                try {
                                  JSON.parse(value);
                                  return Promise.resolve();
                                } catch {
                                  return Promise.reject(
                                    new Error(t('podExempt.dict.extJsonInvalid') ?? ''),
                                  );
                                }
                              },
                            },
                          ]}
                        >
                          <Input.TextArea
                            rows={3}
                            maxLength={2000}
                            showCount
                            onChange={() => setRawDirty(true)}
                          />
                        </Form.Item>
                      </>
                    ),
                  },
                ]}
              />
            </>
          )}
          {dictType !== 'REASON' && (
            // 属性只对 REASON 有意义；此处只说明，不清空既有 extJson（老数据由迁移脚本收口）
            <div className="mb-4 text-xs text-gray-400">{t('podExempt.dict.noExtProps')}</div>
          )}
          <Form.Item name="status" label={t('podExempt.field.status')}>
            <Select
              options={[
                {value: 1, label: t('podExempt.dict.enabled')},
                {value: 0, label: t('podExempt.dict.disabledStatus')},
              ]}
            />
          </Form.Item>
          <Form.Item name="sortNo" label={t('podExempt.dict.sortNo')}>
            <InputNumber min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PodExemptDictManagePage;
